import { Divider } from '@ksyos/design-system';
import type { IOrthOV1DiagnosticReportCustomControlData } from '../../../../../common/interfaces/ophthalmology/orthO/orthOV1DiagnosticReportCustomControlData';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IServiceGetOne } from '../../../interfaces/serviceGetOne';
import type { IDiagnosticReportAddEdit } from '../../diagnosticReports/DiagnosticReportItemAddEdit';
import { FormContextProvider } from '../../../elements/formContext';
import { InputTextField } from '../../../userDataManagement/InputText';
import { OrthOV1DiagnosticReportAddEditCustomControlDataBeleid } from './OrthOV1DiagnosticReportAddEditCustomControlDataBeleid';
import { OrthOV1DiagnosticReportAddEditCustomControlDataBevindingen } from './OrthOV1DiagnosticReportAddEditCustomControlDataBevindingen';
import { OrthOV1DiagnosticReportAddEditCustomControlDataConclusie } from './OrthOV1DiagnosticReportAddEditCustomControlDataConclusie';
import { OrthOV1DiagnosticReportAddEditCustomControlDataTherapie } from './OrthOV1DiagnosticReportAddEditCustomControlDataTherapie';

export interface IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: IOrthOV1DiagnosticReportCustomControlData;
    diagnosticReport: IDiagnosticReportAddEdit;
    errors: ValidationErrors;
    handleChange: <K extends keyof IDiagnosticReportAddEdit>(
        name: K,
        value: IDiagnosticReportAddEdit[K]
    ) => Promise<void>;
    handleChangeCustomControl: (values: Partial<IOrthOV1DiagnosticReportCustomControlData>) => Promise<void>;
    service: IServiceGetOne;
    validateOne: (name: keyof IDiagnosticReportAddEdit) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function OrthOV1DiagnosticReportAddEditCustomControlFields(
    props: IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'diagnosticReport',
                item: props.customControlData,
                errors: props.errors,
                onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                onDoValidate: async (key) => await props.validateOneCustomControl(key),
            }}
        >
            <OrthOV1DiagnosticReportAddEditCustomControlDataBevindingen {...props} />
            <OrthOV1DiagnosticReportAddEditCustomControlDataConclusie {...props} />
            <OrthOV1DiagnosticReportAddEditCustomControlDataTherapie {...props} />
            <Divider />
            <OrthOV1DiagnosticReportAddEditCustomControlDataBeleid {...props} />

            <InputTextField
                error={props.errors.get('customControlData.overige_relevante_informatie')}
                fieldKey="overige_relevante_informatie"
                hasErrors={props.errors.has('customControlData.overige_relevante_informatie')}
                label="diagnosticReport.otherRelevantInformation"
                placeholder="Opmerking of toelichting..."
                value={props.customControlData.overige_relevante_informatie}
            />
        </FormContextProvider>
    );
}

export const orthoptischeDiagnosisInitialValues = {
    refractiestoornis: false,
    refractiestoornis_hypermetropie: false,
    refractiestoornis_myopie: false,
    refractiestoornis_anisometropie: false,
    refractiestoornis_astigmatisme: false,
    insufficientie_en_overmatige_convergentie: false,
    strabismus: false,
    amblyopie: false,
    anders: false,
};

export const therapieInitialValues = {
    toelichting_therapie: null,
    brilrecept_as_od: null,
    brilrecept_cylindrisch_od: null,
    brilrecept_sferisch_od: null,
    brilrecept_additie_od: null,
    brilrecept_prisma_od: null,
    brilrecept_prisma_degrees_od: null,
    brilrecept_as_os: null,
    brilrecept_cylindrisch_os: null,
    brilrecept_sferisch_os: null,
    brilrecept_additie_os: null,
    brilrecept_prisma_os: null,
    brilrecept_prisma_degrees_os: null,
};

export const beleidInitialValues = {
    toelichting_beleid: null,
    termijn: null,
    toelichting_urgentie: null,
    urgentie_oogarts: null,
    urgentie_optometrist: null,
};

export function orthOV1DiagnosticReportInitializeCustomControlData(): IOrthOV1DiagnosticReportCustomControlData {
    return {
        bevindingen: {
            refractieafwijking: false,
            amblyopie: false,
            strabismus: false,
            diplopie: false,
            accommodatie_problemen: false,
            convergentie_insufficientie: false,
            asthopene_klachten: false,
            geen_afwijkingen: false,
            anders: false,
            andersText: null,
        },
        heb_je_een_orthoptische_diagnose: null,
        orthoptische_diagnose: orthoptischeDiagnosisInitialValues,
        therapie: null,
        geadviseerd_beleid: null,
        overige_relevante_informatie: null,
        ...therapieInitialValues,
        ...beleidInitialValues,
    };
}
